<script setup>
import { ref, watch, watchEffect, onMounted } from 'vue';
import { getFromAPI } from '../../functionCollection.js';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css'
import axios from 'axios';

const props = defineProps(['changeTarifUrl', 'getTeilnehmerUrl', 'isAdmin', 'teilnehmerID']);
const emit = defineEmits(['tarifChanged']);

const teilnehmerInfo = ref(null);
const checked = ref(false);
const forced = ref(false);
const changeDate = ref('');
const result = ref(null);
const newTarifNumber = ref('');
const message = ref(null);
const isError = ref(false);
const showMessage = ref(false);
const isCheckingDate = ref(false);

function resetForm() {
    checked.value = false;
    forced.value = false;
    changeDate.value = findNextNoticeDay();
    result.value = null;
    newTarifNumber.value = '';
    message.value = null;
    isError.value = false;
    showMessage.value = false;
}

watchEffect(async () => {
    teilnehmerInfo.value = await getFromAPI(props.getTeilnehmerUrl.replace(':Teilnehmer', props.teilnehmerID));
    changeDate.value = findNextNoticeDay();
    // Autofill for admin
    if (props.isAdmin === 'true' && teilnehmerInfo.value) {
        newTarifNumber.value = teilnehmerInfo.value.Tarifnummer;
    }
});

function findNextNoticeDay() {
    const today = new Date();
    var noticeDate = new Date(today.getFullYear(), today.getMonth()+2, 0);
    return noticeDate;
}

watch([isError, result], ([newError, newResult]) => {
  if (newError || newResult) {
    showMessage.value = true;
    setTimeout(() => {
      showMessage.value = false;
      isError.value = null;
      result.value = null;
    }, 4000);
  }
});

// Reset form when modal opens
onMounted(() => {
    const modal = document.getElementById('modalTeilnehmerTarifChange');
    if (modal) {
        modal.addEventListener('show.bs.modal', () => {
            resetForm();
        });
    }
});

async function checkChangeDateAllowed() {
    isCheckingDate.value = true;
    try {
        const response = await axios.post('/api/teilnehmer/isChangeDateAllowed', {
            rechnungsempfaenger: teilnehmerInfo.value.Rechnungsempfaenger,
            teilnehmer: teilnehmerInfo.value.Teilnehmer,
            tarifnummer: newTarifNumber.value,
            changeDate: changeDate.value
        });
        isCheckingDate.value = false;
        return response.data.allowed;
    } catch (err) {
        isCheckingDate.value = false;
        if (props.isAdmin !== 'true') {
            message.value = 'Bitte wenden Sie sich an den Support, falls Sie glauben, dass ein Fehler vorliegt.';
        } else {
            message.value = 'Das gewählte Änderungsdatum ist nicht erlaubt, da das Datum nicht innerhalb der Vertragslaufzeit liegt.';
        }
        isError.value = true;
        showMessage.value = true;
        return false;
    }
}

async function submit() {
    try {
        if (!newTarifNumber.value) {
            message.value = 'Bitte wählen Sie eine gültige Tarifoption aus.';
            isError.value = true;
            showMessage.value = true;
            return;
        }

        const allowed = await checkChangeDateAllowed();
        if (!allowed) {
            if (props.isAdmin === 'true') {
                message.value = 'Das gewählte Änderungsdatum ist nicht erlaubt, da das Datum nicht innerhalb der Vertragslaufzeit liegt.';
            } else {
                message.value = 'Bitte wenden Sie sich an den Support, falls Sie glauben, dass ein Fehler vorliegt.';
            }
            isError.value = true;
            showMessage.value = true;
            return;
        }

        const response = await axios({
            method: 'put',
            url: props.changeTarifUrl,
            data: {
                Rechnungsempfaenger: teilnehmerInfo.value.Rechnungsempfaenger,
                Teilnehmer: teilnehmerInfo.value.Teilnehmer,
                Tarifnummer: newTarifNumber.value,
                changeDate: changeDate.value,
                forced: forced.value
            }
        });

        message.value = response.data.message;
        isError.value = false;
        showMessage.value = true;

        setTimeout(() => {
            emit('tarifChanged', { reload: true });
            // Close modal after 2 seconds
            const modal = document.getElementById('modalTeilnehmerTarifChange');
            if (modal) {
                const bsModal = window.bootstrap ? window.bootstrap.Modal.getInstance(modal) : null;
                if (bsModal) bsModal.hide();
            }
        }, 2000);

    } catch (err) {
        message.value = err.response?.data?.message || 'Ein unbekannter Fehler ist aufgetreten';
        isError.value = true;
        showMessage.value = true;
    }
}
</script>

<template>

    <form v-if="teilnehmerInfo">
        <div class="form-group row" v-if="props.isAdmin === 'true'">
            <label for="Rechnungsempfaenger" class="col-sm-6 col-form-label">Rechnungsempfänger: </label>
            <div class="col-sm-6">
                <p class="form-control">{{ teilnehmerInfo.Rechnungsempfaenger }}</p>
            </div>
        </div>

        <div class="form-group row" v-if="props.isAdmin === 'true'">
            <label for="Teilnehmer" class="col-sm-6 col-form-label">Teilnehmer</label>
            <div class="col-sm-6">
                <p class="form-control">{{ teilnehmerInfo.Teilnehmer }}</p>
            </div>
        </div>

        <div class="form-group row" v-if="props.isAdmin === 'true'">
            <label for="changeDate" class="col-sm-6 col-form-label">Ändern ab (freie Auswahl):</label>
            <div class="col-sm-6">
                <VueDatePicker
                    v-model="changeDate"
                    :min-date="(new Date())"
                    text-input
                    format="dd.MM.yyyy"
                    locale="de"
                    :enable-time-picker="false"
                    selectText="Auswählen"
                    cancelText="Abbrechen"
                />
            </div>
        </div>

        <div class="form-group row" v-if="props.isAdmin === 'false'">
            <label for="changeDate" class="col-sm-6 col-form-label">Ändern ab (zur nächsten Kündigungsfrist):</label>
            <div class="col-sm-6">
                <VueDatePicker
                    v-model="changeDate"
                    :min-date="findNextNoticeDay()"
                    text-input
                    format="dd.MM.yyyy"
                    locale="de"
                    :enable-time-picker="false"
                    selectText="Auswählen"
                    cancelText="Abbrechen"
                />
            </div>
        </div>

        <div class="form-group row" v-if="props.isAdmin === 'true'">
            <label for="Tarifnummer" class="col-sm-6 col-form-label">Tarifnummer (aktuell {{ teilnehmerInfo.Tarifnummer }}):</label>
            <div class="col-sm-6">
                <input type="text" class="form-control" v-model="newTarifNumber" />
            </div>
        </div>

        <div class="form-group row" v-if="props.isAdmin === 'false' && teilnehmerInfo && teilnehmerInfo.Tarifnummer !== '70'">
            <label for="Tarifnummer" class="col-sm-6 col-form-label">Tarifoption</label>
            <select
                id="tarifchoice-selectpicker"
                name="Tarifoption"
                class="form-select form-control col"
                v-model="newTarifNumber">
                <option value="70">Aktuellen Tarif beenden, Kurzparker werden</option>
                <option value="84">Tarif kündigen</option>
            </select>
        </div>

        <div class="form-group row" v-if="teilnehmerInfo && props.isAdmin === 'false' && teilnehmerInfo.Tarifnummer === '70'">
            <label for="Tarifnummer" class="col-sm-6 col-form-label">Tarifoption (aktuell: Kurzparker)</label>
            <select
                id="tarifchoice-selectpicker"
                name="Tarifoption"
                class="form-select form-control col"
                v-model="newTarifNumber">
                <option value="84" selected>Tarif kündigen</option>
            </select>
        </div>
        <br>

        <div class="horizontal-check" v-if="props.isAdmin === 'true'">
            <div   class="checkbox-container" >
                <input type="checkbox" id="forced_cancellation" v-model="forced">
                <div class="check"></div>
            </div>
            <label  for="forced_cancellation"  >Zwangskündigung</label>
        </div>

        <br>
         <div  class="horizontal-check">
            <div class="checkbox-container">

                <input
                    type="checkbox"
                    class="form-control"
                    id="visibility-checkbox"
                    v-model="checked"/>
                <div class="check"></div>
            </div>
            <label for="visibility"   >Sind Sie sicher, dass Sie Ihren Vertrag umstellen bzw. kündigen wollen?</label>
        </div>

        <br>
        <button type="button" class="btn btn-primary" :disabled="!checked" @click="submit">
            Bestätigen
        </button>
        <transition name="fade">
        <div v-if="showMessage"
             :class="['alert', isError ? 'alert-danger' : 'alert-success']"
             role="alert">
            {{ message }}
        </div>
        </transition>
    </form>

</template>

<style scoped>
.alert {
  z-index: 1000;
  min-width: 300px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.horizontal-check{
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: flex-start;
    gap: 10px;
}



</style>
